/**
 * shared/recipeParser.ts
 *
 * Heuristic recipe text parser shared between client and server. No DOM, no
 * client store imports, so it can be bundled into the Node server without
 * pulling 9000 lines of unrelated code.
 *
 * The client wrapper at client/src/lib/recipeParser.ts re-exports these
 * symbols so existing import sites keep working.
 */

export interface RecipeIngredient {
  qty: number;
  unit: string;
  item: string;
}

export interface ParsedRecipe {
  title: string;
  description: string;
  servings: number;
  durationMin: number;
  ingredients: RecipeIngredient[];
  steps: string[];
}

const INGREDIENTS_HEADERS = /^(ingredients|you'?ll need|what you need|shopping list)\s*[:\-]?$/i;
const STEPS_HEADERS = /^(instructions|steps|directions|method|preparation|how to make( it)?|to make)\s*[:\-]?$/i;
const NOTES_HEADERS = /^(notes|tips|optional|to serve|nutrition)\s*[:\-]?$/i;

const QTY_REGEX = /^(\d+\s*\d*\/?\d*|\d+\.\d+|\d+)\s*([a-zA-Z]+\.?)?\s*(.+)$/;
const NUMERIC_LEAD = /^[\s\-•*\u2022\u2023\u25E6\u2043]*\d/;
const STEP_NUMBER = /^\s*\d+[\.\)\:]\s*/;

export function parseRecipeText(input: string): ParsedRecipe {
  const cleaned = input
    .replace(/\r\n/g, "\n")
    .replace(/\u2028|\u2029/g, "\n")
    .trim();

  if (!cleaned) return emptyDraft();

  const lines = cleaned
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .filter((l) => !/^#[\w-]+(\s+#[\w-]+)*\s*$/.test(l));

  const sections = splitBySections(lines);
  const title = pickTitle(lines, sections);
  const { ingredients, steps } = sections.foundLabels
    ? {
        ingredients: parseIngredientLines(sections.ingredientsLines),
        steps: parseStepLines(sections.stepsLines),
      }
    : heuristicSplit(lines, title);

  const servings = extractServings(cleaned);
  const durationMin = extractDuration(cleaned);
  const description = sections.introLines.slice(0, 2).join(" ").trim();

  return {
    title: title || "Captured recipe",
    description,
    servings: servings ?? 4,
    durationMin: durationMin ?? 30,
    ingredients: ingredients.length ? ingredients : [{ qty: 0, unit: "", item: "Add ingredients here" }],
    steps: steps.length ? steps : ["Add instructions here"],
  };
}

function emptyDraft(): ParsedRecipe {
  return {
    title: "Captured recipe",
    description: "",
    servings: 4,
    durationMin: 30,
    ingredients: [{ qty: 0, unit: "", item: "Add ingredients here" }],
    steps: ["Add instructions here"],
  };
}

interface SectionSplit {
  foundLabels: boolean;
  introLines: string[];
  ingredientsLines: string[];
  stepsLines: string[];
}

function splitBySections(lines: string[]): SectionSplit {
  const intro: string[] = [];
  const ing: string[] = [];
  const stp: string[] = [];
  let mode: "intro" | "ing" | "stp" | "notes" = "intro";
  let foundLabels = false;
  for (const line of lines) {
    if (INGREDIENTS_HEADERS.test(line)) { mode = "ing"; foundLabels = true; continue; }
    if (STEPS_HEADERS.test(line)) { mode = "stp"; foundLabels = true; continue; }
    if (NOTES_HEADERS.test(line)) { mode = "notes"; foundLabels = true; continue; }
    if (mode === "intro") intro.push(line);
    else if (mode === "ing") ing.push(line);
    else if (mode === "stp") stp.push(line);
  }
  return { foundLabels, introLines: intro, ingredientsLines: ing, stepsLines: stp };
}

function pickTitle(lines: string[], sections: SectionSplit): string {
  const candidate = sections.introLines[0] ?? lines[0] ?? "";
  const trimmed = candidate
    .replace(/^["']|["']$/g, "")
    .replace(/[\uD83C-\uDBFF\uDC00-\uDFFF]+/g, "")
    .replace(/[\u2600-\u27BF]+/g, "")
    .trim();
  if (trimmed.length > 0 && trimmed.length <= 90) return trimmed;
  return trimmed.slice(0, 60).trim();
}

const UNIT_WORDS = new Set([
  "cup", "cups", "c",
  "tbsp", "tablespoon", "tablespoons", "tbs", "tbl",
  "tsp", "teaspoon", "teaspoons",
  "oz", "ounce", "ounces",
  "lb", "lbs", "pound", "pounds",
  "g", "gram", "grams",
  "kg", "kilogram", "kilograms",
  "ml", "milliliter", "milliliters",
  "l", "liter", "liters",
  "pinch", "dash", "handful", "clove", "cloves", "sprig", "sprigs",
  "can", "cans", "package", "packages", "pkg", "stick", "sticks",
  "slice", "slices",
]);

function parseIngredientLines(lines: string[]): RecipeIngredient[] {
  return lines
    .map((raw) => raw.replace(/^[\-•*\u2022\u2023\u25E6\u2043]\s*/, "").trim())
    .filter((l) => l.length > 0 && l.length < 200)
    .map(parseIngredientLine);
}

function parseIngredientLine(line: string): RecipeIngredient {
  const m = line.match(QTY_REGEX);
  if (!m) return { qty: 0, unit: "", item: line };
  const qtyStr = m[1].trim();
  const possibleUnit = (m[2] ?? "").replace(/\.$/, "").toLowerCase();
  const rest = m[3].trim();
  const qty = parseQuantity(qtyStr);
  if (UNIT_WORDS.has(possibleUnit)) return { qty, unit: possibleUnit, item: rest };
  const item = (m[2] ? `${m[2]} ${rest}` : rest).trim();
  return { qty, unit: "", item };
}

function parseQuantity(s: string): number {
  const trimmed = s.trim();
  if (trimmed.includes("/")) {
    const parts = trimmed.split(/\s+/);
    if (parts.length === 2) {
      const whole = Number(parts[0]);
      const [num, den] = parts[1].split("/").map(Number);
      if (Number.isFinite(whole) && den) return whole + num / den;
    }
    const [num, den] = trimmed.split("/").map(Number);
    if (den) return num / den;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

function parseStepLines(lines: string[]): string[] {
  const cleaned = lines
    .map((l) => l.replace(STEP_NUMBER, "").trim())
    .filter((l) => l.length > 0);
  if (cleaned.length === 1 && cleaned[0].length > 160) return splitIntoSentences(cleaned[0]);
  return cleaned;
}

function splitIntoSentences(s: string): string[] {
  return s.split(/(?<=[.!?])\s+(?=[A-Z])/).map((p) => p.trim()).filter((p) => p.length > 0);
}

function heuristicSplit(lines: string[], title: string): { ingredients: RecipeIngredient[]; steps: string[] } {
  const body = lines.filter((l) => l !== title);
  const ing: string[] = [];
  const stp: string[] = [];
  for (const line of body) {
    if (looksLikeIngredient(line)) ing.push(line);
    else if (line.length > 12) stp.push(line);
  }
  return { ingredients: parseIngredientLines(ing), steps: parseStepLines(stp) };
}

function looksLikeIngredient(line: string): boolean {
  if (line.length > 100) return false;
  if (NUMERIC_LEAD.test(line)) return true;
  if (/^[\-•*]\s+\S+/.test(line) && line.length < 80) return true;
  return false;
}

function extractServings(text: string): number | null {
  const m = text.match(/(?:serves|servings?|yield|makes)\s*[:\-]?\s*(\d+)/i);
  if (m) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > 0 && n < 100) return n;
  }
  return null;
}

function extractDuration(text: string): number | null {
  const hourMin = text.match(/(\d+)\s*h(?:our|r)s?\s*(?:and\s*)?(?:(\d+)\s*m(?:in|inute)s?)?/i);
  if (hourMin) {
    const h = Number(hourMin[1]) || 0;
    const m = Number(hourMin[2]) || 0;
    const total = h * 60 + m;
    if (total > 0 && total < 600) return total;
  }
  const min = text.match(/(\d+)\s*m(?:in|inute)s?\b/i);
  if (min) {
    const n = Number(min[1]);
    if (Number.isFinite(n) && n > 0 && n < 600) return n;
  }
  return null;
}
